import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Form, Button } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import StockKeeperProductsTableComponent from "../../components/tables/StockKeeper/StockKeeperProductsTableComponent";
import LabelFieldComponent from "../../components/fields/LabelFieldComponent";
import PageLayout from "../../layouts/PageLayout";

export default function StockKeeperProductListPage() {
    const { t, n } = useContext(TranslatorContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [activeTab, setActiveTab] = useState("new");

    const [allProducts, setAllProducts] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);

    const [categoriesOptions, setCategoriesOptions] = useState([]);
    const [brandsOptions, setBrandsOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedBrand, setSelectedBrand] = useState("");

    const [counts, setCounts] = useState({ new: 0, recieved: 0 });

    const tableHeaders = [
        ...(activeTab === "new" ? ["Select"] : []),
        "Product Code",
        "Title",
        "Detail",
        "Seller Price",
        "Action",
    ];

    const getAuthHeader = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    });

    const extractData = (res) => {
        if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
        if (Array.isArray(res?.data)) return res.data;
        return [];
    };

    const fetchInitialFilters = useCallback(async () => {
        try {
            const baseUrl = process.env.REACT_APP_API_URL;
            const response = await axios.get(`${baseUrl}/stock-keeper/initial`, getAuthHeader());
            const resData = response.data?.data || response.data;
            if (resData?.categories) setCategoriesOptions(resData.categories);
            if (resData?.brands) setBrandsOptions(resData.brands);
        } catch (err) {
            console.error("Error fetching filters:", err);
        }
    }, []);

    const fetchCounts = useCallback(async () => {
        try {
            const baseUrl = process.env.REACT_APP_API_URL;
            const [newRes, recievedRes] = await Promise.all([
                axios.get(`${baseUrl}/stock-keeper/products/new-products`, getAuthHeader()),
                axios.get(`${baseUrl}/stock-keeper/products/recieved-products`, getAuthHeader()),
            ]);

            setCounts({
                new: extractData(newRes).length,
                recieved: extractData(recievedRes).length,
            });
        } catch (err) {
            console.error("Error fetching counts:", err);
        }
    }, []);

    const fetchTabData = useCallback(async (tab) => {
        setLoading(true);
        setError(null);
        setSelectedProductIds([]);

        const endpoint = tab === "new" ? "/stock-keeper/products/new-products" : "/stock-keeper/products/recieved-products";

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}${endpoint}`, getAuthHeader());
            const listData = extractData(response);
            setAllProducts(listData);
            setProductsList(listData);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch products.");
            setAllProducts([]);
            setProductsList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tabParam = queryParams.get("tab");
        if (tabParam && ["new", "recieved"].includes(tabParam)) {
            setActiveTab(tabParam);
        }
    }, [location.search]);

    useEffect(() => {
        fetchInitialFilters();
        fetchCounts();
    }, [fetchInitialFilters, fetchCounts]);

    useEffect(() => {
        fetchTabData(activeTab);
    }, [activeTab, fetchTabData]);

    useEffect(() => {
        let filtered = [...allProducts];
        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(
                (p) => (p.title && p.title.toLowerCase().includes(query)) || (p.product_code && p.product_code.toLowerCase().includes(query))
            );
        }
        if (selectedCategory) {
            filtered = filtered.filter((p) => String(p.category_id || p.category?.id) === String(selectedCategory));
        }
        if (selectedBrand) {
            filtered = filtered.filter((p) => String(p.brand_id || p.brand?.id) === String(selectedBrand));
        }
        setProductsList(filtered);
    }, [allProducts, searchTerm, selectedCategory, selectedBrand]);

    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        setSuccessMessage(null);
        setError(null);
        navigate(`/stock-keeper-products?tab=${tabKey}`);
    };

    const handleSingleReceived = async (id) => {
        try {
            setSuccessMessage(null);
            setError(null);
            const baseUrl = process.env.REACT_APP_API_URL;
            const res = await axios.post(`${baseUrl}/stock-keeper/products/recieve-products/${id}`, {}, getAuthHeader());

            setSuccessMessage(res.data?.message || "Product received successfully!");
            fetchTabData(activeTab);
            fetchCounts();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to update product.");
        }
    };

    const handleBulkReceived = async () => {
        if (selectedProductIds.length === 0) return;
        try {
            setSuccessMessage(null);
            setError(null);
            const baseUrl = process.env.REACT_APP_API_URL;
            await Promise.all(
                selectedProductIds.map((id) =>
                    axios.post(`${baseUrl}/stock-keeper/products/recieve-products/${id}`, {}, getAuthHeader())
                )
            );

            setSuccessMessage(`Successfully received ${selectedProductIds.length} product(s)!`);
            setSelectedProductIds([]);
            fetchTabData(activeTab);
            fetchCounts();
        } catch (err) {
            setError("Failed to update selected products.");
        }
    };

    const formatSelectOptions = (list) => list.map(item => ({ value: String(item.id), label: item.name }));

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <h3>{t("stock_keeper_products")} ({productsList.length})</h3>
                    </div>
                </Col>

                <Col sm={6} className="mb-3" onClick={() => handleTabChange("new")} style={{ cursor: "pointer" }}>
                    <FloatCardComponent
                        variant={activeTab === "new" ? "lg blue active-card" : "lg blue"}
                        digit={n ? n(counts.new) : counts.new}
                        title="New Products"
                        icon="shopping_bag"
                    />
                </Col>
                <Col sm={6} className="mb-3" onClick={() => handleTabChange("recieved")} style={{ cursor: "pointer" }}>
                    <FloatCardComponent
                        variant={activeTab === "recieved" ? "lg purple active-card" : "lg purple"}
                        digit={n ? n(counts.recieved) : counts.recieved}
                        title="Received Products"
                        icon="inventory"
                    />
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                        {successMessage && <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

                        {activeTab === "new" && selectedProductIds.length > 0 && (
                            <div className="mb-3">
                                <Button variant="success" onClick={handleBulkReceived}>
                                    Receive Selected Products ({selectedProductIds.length})
                                </Button>
                            </div>
                        )}

                        <Row className="mb-4 g-3 align-items-end">
                            <Col md={4}>
                                <LabelFieldComponent type="search" placeholder={t("search title or code")} label={t("search")} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>{t("category")}</Form.Label>
                                    <Select isClearable options={formatSelectOptions(categoriesOptions)} onChange={(opt) => setSelectedCategory(opt ? opt.value : "")} />
                                </Form.Group>
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5"><Spinner animation="border" /></div>
                        ) : (
                            <StockKeeperProductsTableComponent
                                thead={tableHeaders}
                                tbody={productsList}
                                activeTab={activeTab}
                                selectedProductIds={selectedProductIds}
                                setSelectedProductIds={setSelectedProductIds}
                                onSingleReceived={handleSingleReceived}
                            />
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}